import { DatePipe, DecimalPipe } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withPreloading } from '@angular/router';
import {
  GlobalErrorHandler,
  handleAuthChange,
  handleUserLoggedOut,
  handleUserSwitchingAccounts,
  registerGlobalEffects,
  SelectAllBulkActionService,
  TrackjsErrorInterceptor,
} from '@libs/account-manager';
import { dispatch } from '@ngneat/effects';
import {
  configureDataAccessEnvironment,
  DisplayDateTimePipe,
  provideRootTranslocoConfig,
  TruncateStringPipe,
} from '@shared/core-utils';
import { environmentProvider } from '@shared/environment-provider';
import { provideAuthConfig } from '@tmx/auth';
import { provideAppVersion } from '@tmx/core';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalRef, ModalModule } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { VERSION } from '../version';
import { appRoutes } from './app.routes';
import { CustomPreloadingStrategy } from './custom-preloading-strategy';

const { productName, tid } =
  environmentProvider.getEnvironment().accountManagerSite;
const { appName, clientId } = tid;

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      BrowserModule,
      ReactiveFormsModule,
      PopoverModule.forRoot(),
      ModalModule.forRoot(),
      CollapseModule.forRoot(),
      TooltipModule.forRoot(),
      TabsModule.forRoot(),
      BsDatepickerModule.forRoot(),
      BsDropdownModule.forRoot()
    ),
    provideRouter(appRoutes, withPreloading(CustomPreloadingStrategy)),
    provideRootTranslocoConfig(),
    configureDataAccessEnvironment(),
    BsModalRef,
    DecimalPipe,
    DatePipe,
    TruncateStringPipe,
    DisplayDateTimePipe,
    provideAppInitializer(() => {
      registerGlobalEffects();
    }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TrackjsErrorInterceptor,
      multi: true,
    },
    provideAuthConfig({
      eventHandlers: {
        onAuthenticated: (authInfo) => {
          dispatch(handleAuthChange(authInfo));
        },
        onLoggedOut: () => {
          dispatch(handleUserLoggedOut());
        },
        onSwitchingAccount: () => {
          dispatch(handleUserSwitchingAccounts());
        },
      },
      trimbleIdentity: {
        trimbleCloudAppName: appName,
        trimbleCloudClientId: clientId,
        trimbleMapsAppName: productName,
      },
    }),
    provideAnimations(),
    provideAppVersion(VERSION),
    provideAppInitializer(() => {
      inject(SelectAllBulkActionService);
    }),
  ],
};
